import type {
  LockerverseCatalog,
  LockerverseCheckoutClient,
  LockerverseCheckoutEstimate,
  LockerverseCheckoutItem,
  LockerversePreparedCheckout,
} from "@lockerverse/sdk/checkout";
import { estimateLockerverseCheckout } from "@lockerverse/sdk/checkout";
import { useEffect, useMemo, useReducer, useRef } from "react";
import type { PaymentUiState } from "./payment-form-state.ts";
import { parsePaymentSelection } from "./payment-input.ts";
import type {
  LockerversePaymentProps,
  LockerversePaymentSelectionItem,
} from "./types.ts";

type SessionState =
  | {
      phase: "loading";
      revision: number;
    }
  | {
      failure: string;
      phase: "error";
      revision: number;
    }
  | {
      checkoutItems: LockerverseCheckoutItem[];
      displayedQuote: LockerverseCheckoutEstimate | LockerversePreparedCheckout;
      phase: "ready";
      quoteFailure: string | undefined;
      revision: number;
    };

type SessionAction =
  | {
      type: "loading";
    }
  | {
      displayedQuote: LockerverseCheckoutEstimate | LockerversePreparedCheckout;
      type: "ready";
    }
  | {
      failure: string;
      type: "error";
    }
  | { checkout: LockerversePreparedCheckout; type: "checkout" }
  | { failure: string; type: "quote-error" };

function sessionReducer(
  state: SessionState,
  action: SessionAction
): SessionState {
  switch (action.type) {
    case "loading":
      return {
        phase: "loading",
        revision: state.revision,
      };
    case "ready":
      return createReadySession(action.displayedQuote, state.revision + 1);
    case "error":
      return {
        failure: action.failure,
        phase: "error",
        revision: state.revision,
      };
    case "checkout":
      return state.phase === "ready"
        ? {
            ...state,
            displayedQuote: action.checkout,
            quoteFailure: undefined,
          }
        : state;
    case "quote-error":
      return state.phase === "ready"
        ? {
            ...state,
            quoteFailure: action.failure,
          }
        : state;
    default:
      return state;
  }
}

type LoadIdentity = {
  catalog: LockerverseCatalog;
  selectionKey: string;
};

function isSameLoad(
  identity: LoadIdentity | undefined,
  catalog: LockerverseCatalog,
  selectionKey: string
) {
  return (
    identity?.catalog === catalog && identity.selectionKey === selectionKey
  );
}

function createReadyAction({
  checkout,
}: {
  checkout: LockerverseCheckoutEstimate | LockerversePreparedCheckout;
}): Extract<SessionAction, { type: "ready" }> {
  return {
    displayedQuote: checkout,
    type: "ready",
  };
}

function getLoadFailure<ErrorValue>(error: ErrorValue) {
  return error instanceof Error
    ? error.message
    : "Lockerverse payment could not load.";
}

function createReadySession(
  checkout: LockerverseCheckoutEstimate | LockerversePreparedCheckout,
  revision: number
): SessionState {
  return {
    checkoutItems: [...checkout.items],
    displayedQuote: checkout,
    phase: "ready",
    quoteFailure: undefined,
    revision,
  };
}

function estimateCheckout(
  catalog: LockerverseCatalog,
  selection: LockerversePaymentSelectionItem[]
) {
  const includeServiceFee = catalog.widget.fields.serviceFee;
  return estimateLockerverseCheckout(catalog, {
    includeServiceFee,
    selection,
  });
}

function createInitialSession(
  catalog: LockerverseCatalog,
  selection: LockerversePaymentSelectionItem[]
): SessionState {
  try {
    return createReadySession(estimateCheckout(catalog, selection), 1);
  } catch (error) {
    return {
      failure: getLoadFailure(error),
      phase: "error",
      revision: 0,
    };
  }
}

export function usePaymentCheckout({
  catalog,
  client,
  paymentState,
  selection,
}: {
  catalog: LockerverseCatalog;
  client: LockerverseCheckoutClient;
  paymentState: PaymentUiState;
  selection: LockerversePaymentProps["selection"];
}) {
  const parsedSelection = parsePaymentSelection(selection);
  const selectionKey = parsedSelection.success
    ? JSON.stringify(parsedSelection.value)
    : "invalid-selection";
  const stableSelection = useMemo(
    () => (parsedSelection.success ? parsedSelection.value : []),
    [parsedSelection.success, selectionKey]
  );
  const [session, dispatch] = useReducer(
    sessionReducer,
    undefined,
    (): SessionState =>
      parsedSelection.success
        ? createInitialSession(catalog, stableSelection)
        : { phase: "loading", revision: 0 }
  );
  const loadingRef = useRef<LoadIdentity | undefined>(undefined);
  const quoteRevisionRef = useRef(0);
  const estimatedRef = useRef<LoadIdentity | undefined>(
    session.phase === "ready" ? { catalog, selectionKey } : undefined
  );
  const settledRef = useRef<LoadIdentity | undefined>(
    session.phase === "error" ? { catalog, selectionKey } : undefined
  );
  const sessionIsLocked =
    paymentState === "submitting" ||
    paymentState === "success" ||
    paymentState === "unresolved";
  useEffect(() => {
    if (!parsedSelection.success) {
      return;
    }
    if (
      sessionIsLocked ||
      isSameLoad(settledRef.current, catalog, selectionKey) ||
      isSameLoad(loadingRef.current, catalog, selectionKey)
    ) {
      return;
    }

    let active = true;
    loadingRef.current = { catalog, selectionKey };
    let estimateIsReady = isSameLoad(
      estimatedRef.current,
      catalog,
      selectionKey
    );
    if (!estimateIsReady) {
      dispatch({ type: "loading" });
    }

    function dispatchWhenActive(action: SessionAction) {
      if (active) {
        dispatch(action);
      }
    }

    function settleWhenActive() {
      if (active) {
        loadingRef.current = undefined;
        settledRef.current = { catalog, selectionKey };
      }
    }

    let initialQuoteRevision: number | undefined;

    function failWhenActive<ErrorValue>(error: ErrorValue) {
      if (!active) {
        return;
      }
      loadingRef.current = undefined;
      settledRef.current = { catalog, selectionKey };
      if (
        estimateIsReady &&
        initialQuoteRevision !== undefined &&
        quoteRevisionRef.current !== initialQuoteRevision
      ) {
        return;
      }
      const failure = getLoadFailure(error);
      if (failure === "This option is sold out.") {
        dispatch({ failure, type: "error" });
        return;
      }
      if (estimateIsReady) {
        dispatch({ failure, type: "quote-error" });
      } else {
        dispatch({
          failure,
          type: "error",
        });
      }
    }

    async function loadPayment() {
      try {
        initialQuoteRevision = quoteRevisionRef.current;
        const includeServiceFee = catalog.widget.fields.serviceFee;
        if (!estimateIsReady) {
          const estimate = estimateCheckout(catalog, stableSelection);
          estimateIsReady = true;
          estimatedRef.current = { catalog, selectionKey };
          dispatchWhenActive(createReadyAction({ checkout: estimate }));
        }
        const preparedCheckout = await client.calculateTotal({
          catalog,
          includeServiceFee,
          selection: stableSelection,
        });
        settleWhenActive();
        if (quoteRevisionRef.current === initialQuoteRevision) {
          dispatchWhenActive({
            checkout: preparedCheckout,
            type: "checkout",
          });
        }
      } catch (error) {
        failWhenActive(error);
      }
    }

    loadPayment();
    return () => {
      active = false;
      if (isSameLoad(loadingRef.current, catalog, selectionKey)) {
        loadingRef.current = undefined;
      }
    };
  }, [
    catalog,
    client,
    parsedSelection.success,
    sessionIsLocked,
    selectionKey,
    stableSelection,
  ]);

  const loadIsCurrent =
    isSameLoad(estimatedRef.current, catalog, selectionKey) ||
    isSameLoad(settledRef.current, catalog, selectionKey) ||
    isSameLoad(loadingRef.current, catalog, selectionKey);
  const currentSession =
    sessionIsLocked || loadIsCurrent
      ? session
      : ({ phase: "loading", revision: session.revision } as const);

  return {
    selectionError: parsedSelection.success
      ? undefined
      : parsedSelection.message,
    session: currentSession,
    updateCheckout: (checkout: LockerversePreparedCheckout) => {
      quoteRevisionRef.current += 1;
      dispatch({ checkout, type: "checkout" });
    },
  };
}
